import { apiClient } from './api'

export type LogEntry = {
  id: string
  level: 'debug' | 'info' | 'warning' | 'error' | 'critical'
  source: string
  action: string
  message: string
  details: Record<string, any>
  ip_address?: string
  user_agent?: string
  request_id?: string
  resource_type?: string
  resource_id?: string
  created_at: string
  user?: {
    id: string
    email: string
    full_name: string
  }
  organization?: {
    id: string
    name: string
    type: string
  }
}

export type LogsStats = {
  period: {
    start: string
    end: string
    hours: number
  }
  by_level: Record<string, number>
  by_source: Record<string, number>
  hourly_activity: {
    hour: string
    count: number
  }[]
  recent_errors: {
    id: string
    level: string
    source: string
    message: string
    created_at: string
  }[]
  total_logs: number
}

export type LogsFilters = {
  skip?: number
  limit?: number
  level?: string
  source?: string
  search?: string
  start_date?: string
  end_date?: string
  user_id?: string
  organization_id?: string
  task_id?: string
  resource_type?: string
  resource_id?: string
}

export const logsApi = {
  // Get system logs with filters
  getLogs: (filters: LogsFilters = {}): Promise<LogEntry[]> => {
    const params = new URLSearchParams()

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString())
      }
    })

    return apiClient.get(`/api/v1/logs?${params}`)
  },

  // Get logs statistics
  getStats: (hours: number = 24): Promise<LogsStats> => {
    return apiClient.get(`/api/v1/logs/stats?hours=${hours}`)
  },

  // Export logs (long operation)
  exportLogs: (format: 'csv' | 'json' = 'csv', filters: LogsFilters = {}): Promise<Blob> => {
    const params = new URLSearchParams({ format })

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString())
      }
    })

    return apiClient.getLong(`/api/v1/logs/export?${params}`, {
      responseType: 'blob',
    })
  },

  // Get organization logs (any authenticated user)
  getOrganizationLogs: (filters: LogsFilters = {}): Promise<LogEntry[]> => {
    const params = new URLSearchParams()

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString())
      }
    })

    return apiClient.get(`/api/v1/logs/organization?${params}`)
  },

  // Create manual log entry (admin only)
  createLog: (logData: {
    level?: string
    source?: string
    action?: string
    message: string
    details?: Record<string, any>
  }): Promise<{ id: string; message: string; created_at: string }> => {
    return apiClient.post('/api/v1/logs', logData)
  },

  // Get available log levels
  getLogLevels: (): string[] => {
    return ['debug', 'info', 'warning', 'error', 'critical']
  },

  // Get available log sources
  getLogSources: (): string[] => {
    return [
      'auth',
      'organization',
      'user',
      'rbac',
      'cluster',
      'inventory',
      'api',
      'system',
      'security',
      'celery',
      'tasks',
      'vm_creation',
      'block_storage',
      'proxmox',
    ]
  },
}

export default logsApi
